import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GET_STAFF_MANAGEMENT_WORKERS } from "graphql/query/staff-management-gql";
import { STAFF_WORKERS_PAGE_SIZE } from "../helpers/staff-workers-api-helper";


const EMPTY_WORKERS = [];
const WORKERS_ORDER_BY = [
    { column: "NAME", order: "ASC" },
    { column: "SURNAME", order: "ASC" },
    { column: "ID", order: "ASC" },
];

const useStaffWorkersForSorting = ({ enabled = false, eeID, text, where }) => {
    const client = useApolloClient();
    const eventID = String(eeID ?? "").trim();
    const shouldLoad = Boolean(enabled && eventID);
    const variablesKey = JSON.stringify({ eeID: [ eventID ], text, where });
    const variables = useMemo(() => JSON.parse(variablesKey), [ variablesKey ]);
    const [ attempt, setAttempt ] = useState(0);
    const context = useMemo(() => ({ client, variables, shouldLoad, attempt }), [ client, variables, shouldLoad, attempt ]);
    const [ state, setState ] = useState(null);
    const retry = useCallback(() => {
        if (shouldLoad) setAttempt((value) => value + 1);
    }, [ shouldLoad ]);

    useEffect(() => {
        let isCurrent = true;
        setState(null);

        if (!shouldLoad) return undefined;

        const loadWorkers = async () => {
            const workers = [];
            const workerIDs = new Set();
            let lastPage = 1;
            let expectedTotal;

            for (let page = 1; page <= lastPage; page += 1)
            {
                const result = await client.query({
                    query: GET_STAFF_MANAGEMENT_WORKERS,
                    variables: { ...variables, first: STAFF_WORKERS_PAGE_SIZE, page, orderBy: WORKERS_ORDER_BY },
                    fetchPolicy: "network-only",
                    errorPolicy: "none",
                });

                // Do not request any further pages for obsolete filters or exhibitions.
                if (!isCurrent) return;

                const users = result?.data?.users;
                const pagination = users?.paginatorInfo;
                if (result?.errors?.length || !Array.isArray(users?.data) || pagination?.currentPage !== page ||
                    !Number.isInteger(pagination?.lastPage) || pagination.lastPage < page ||
                    (page === 1 && (!Number.isInteger(pagination.total) || pagination.total < 0)) ||
                    (page > 1 && pagination.lastPage !== lastPage) ||
                    (users.data.length === 0 && page < pagination.lastPage))
                {
                    throw new Error("Incomplete workers response");
                }

                lastPage = pagination.lastPage;
                if (page === 1) expectedTotal = pagination.total;
                users.data.forEach((worker) => {
                    const workerID = String(worker?.id ?? "").trim();
                    if (!workerID || (typeof worker.id !== "string" && !Number.isFinite(worker.id)))
                    {
                        throw new Error("Incomplete workers response");
                    }

                    if (!workerIDs.has(workerID))
                    {
                        workerIDs.add(workerID);
                        workers.push(worker);
                    }
                });
            }

            if (workers.length !== expectedTotal) throw new Error("Incomplete workers response");

            setState({ context, workers, error: null });
        };

        loadWorkers().catch((error) => {
            if (isCurrent) setState({ context, workers: EMPTY_WORKERS, error });
        });

        return () => { isCurrent = false; };
    }, [ client, context, shouldLoad, variables ]);

    // Hide stale results on the first render, before the new request effect runs.
    const currentState = shouldLoad && state?.context === context ? state : null;

    return {
        workers: currentState?.workers || EMPTY_WORKERS,
        loading: shouldLoad && !currentState,
        error: currentState?.error || null,
        ready: Boolean(currentState && !currentState.error),
        retry,
    };
};

export default useStaffWorkersForSorting;
